import { createSignal, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { Portal } from "solid-js/web";
import { TransitionGroup } from "solid-transition-group";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-solid";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { vibrate } from "@/lib/gestures/haptics";
import { TOAST_SWIPE_DISMISS } from "@/lib/gestures/presets";

type ToastType = "success" | "error" | "info" | "warning";

/** Inline toast action (e.g. "Undo"). */
interface ToastAction {
  label: string;
  onAction: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

const TOAST_DURATION = 5000;
const RADIX_36 = 36;
const ID_LENGTH = 9;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const [toasts, setToasts] = createStore<Toast[]>([]);

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

export function showToast(message: string, type: ToastType = "info") {
  const id = Math.random().toString(RADIX_36).slice(2, ID_LENGTH);
  setToasts([...toasts, { id, message, type }]);

  setTimeout(() => {
    setToasts(toasts.filter((t) => t.id !== id));
  }, TOAST_DURATION);
}

function removeToast(id: string) {
  setToasts(toasts.filter((t) => t.id !== id));
}

const FLASH_ID = "__saved_flash__";
const FLASH_DURATION = 1400;
let flashTimer: ReturnType<typeof setTimeout> | undefined;

const UNDO_FLASH_ID = "__undo_flash__";
const UNDO_FLASH_DURATION = 6000;
let undoFlashTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Coalesced undoable pill (L1): a single "action done · Undo" toast that
 * refreshes in place on each mutation instead of stacking notifications
 * (deleting 5 notes in a row = 1 live pill, not 5 toasts). Auto-dismisses in
 * ~6s; the full history stays in the undo stack (Ctrl+Z steps past the pill).
 */
export function showUndoFlash(message: string, action: ToastAction) {
  if (toasts.some((t) => t.id === UNDO_FLASH_ID)) {
    // Already visible: update in place (no re-animate) and refresh the timer.
    setToasts((t) => t.id === UNDO_FLASH_ID, { message, action });
  } else {
    setToasts([...toasts, { id: UNDO_FLASH_ID, message, type: "success", action }]);
  }
  if (undoFlashTimer) {
    clearTimeout(undoFlashTimer);
  }
  undoFlashTimer = setTimeout(() => {
    dismissUndoFlash();
  }, UNDO_FLASH_DURATION);
}

/** Retires the undo pill (after undoing, or on account switch). */
export function dismissUndoFlash() {
  if (undoFlashTimer) {
    clearTimeout(undoFlashTimer);
    undoFlashTimer = undefined;
  }
  setToasts(toasts.filter((t) => t.id !== UNDO_FLASH_ID));
}

/**
 * Coalesced "saved"/"undone" confirmation: a single short-lived pill that
 * refreshes its timer on each call, giving calm feedback without toast spam.
 */
export function showSavedFlash(message: string) {
  if (toasts.some((t) => t.id === FLASH_ID)) {
    setToasts((t) => t.id === FLASH_ID, "message", message);
  } else {
    setToasts([...toasts, { id: FLASH_ID, message, type: "success" }]);
  }
  if (flashTimer) {
    clearTimeout(flashTimer);
  }
  flashTimer = setTimeout(() => {
    setToasts(toasts.filter((t) => t.id !== FLASH_ID));
  }, FLASH_DURATION);
}

/**
 * A single toast row with swipe-to-dismiss.
 *
 * The whole card tracks the finger 1:1 along x and fades as it travels. A fling
 * or a drag past the commit threshold dismisses it (flinging off-screen in the
 * swipe direction, with a light haptic); otherwise it snaps back. `touch-action:
 * pan-y` lets the recognizer claim the horizontal drag while vertical scroll of
 * the page is preserved. Auto-dismiss and the close button are untouched.
 */
function ToastItem(props: { toast: Toast }) {
  const [dx, setDx] = createSignal(0);
  const [settling, setSettling] = createSignal(false);
  let dismissing = false;

  function settle() {
    setSettling(true);
    if (dismissing) {
      // Fling the card off-screen in the swipe direction, then remove it. The
      // CSS transition (when motion is allowed) animates the slide-out.
      const dir = dx() >= 0 ? 1 : -1;
      setDx(dir * TOAST_SWIPE_DISMISS.flingOutPx);
      vibrate(TOAST_SWIPE_DISMISS.hapticMs);
      const delay = prefersReducedMotion() ? 0 : TOAST_SWIPE_DISMISS.transitionMs;
      setTimeout(() => {
        removeToast(props.toast.id);
      }, delay);
      return;
    }
    setDx(0);
  }

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: TOAST_SWIPE_DISMISS.thresholdPx,
    fling: { ...TOAST_SWIPE_DISMISS.fling },
    onStart: () => {
      dismissing = false;
      setSettling(false);
    },
    onMove: (sample) => {
      setSettling(false);
      setDx(sample.dx);
    },
    onEnd: (sample) => {
      dismissing = sample.distance >= TOAST_SWIPE_DISMISS.commitThresholdPx || sample.isFling;
      settle();
    },
    onCancel: settle,
  });

  const handlers = pointerHandlers(recognizer);

  // Fade out proportionally to the swipe travel so the toast visibly "lets go".
  const opacity = () => {
    const ratio = Math.abs(dx()) / TOAST_SWIPE_DISMISS.commitThresholdPx;
    return Math.max(0, 1 - ratio * 0.7);
  };

  // While the toast sits idle we leave transform/opacity unset so the
  // TransitionGroup enter/exit classes own them (stacking animation intact). We
  // only take over inline once a swipe begins (dragging or settling).
  const isInteracting = () => dx() !== 0 || settling();

  const dragStyle = () => {
    if (!isInteracting()) {
      return { "touch-action": "pan-y" };
    }
    return {
      transform: `translateX(${dx()}px)`,
      opacity: String(opacity()),
      "touch-action": "pan-y",
      transition:
        settling() && !prefersReducedMotion()
          ? `transform ${TOAST_SWIPE_DISMISS.transitionMs}ms cubic-bezier(0.19, 1, 0.22, 1), opacity ${TOAST_SWIPE_DISMISS.transitionMs}ms ease`
          : "none",
    };
  };

  return (
    <div
      // CC-30: error toasts must announce assertively (`role="alert"`) so a
      // failed API action is surfaced to AT immediately; non-errors stay polite.
      role={props.toast.type === "error" ? "alert" : "status"}
      aria-live={props.toast.type === "error" ? "assertive" : "polite"}
      class="flex items-center justify-between gap-3 p-4 rounded-lg border shadow-lg bg-card select-none"
      classList={{
        "border-success/30 bg-success/5 text-success": props.toast.type === "success",
        "border-destructive/30 bg-destructive/5 text-destructive": props.toast.type === "error",
        "border-warning/30 bg-warning/5 text-warning": props.toast.type === "warning",
        "border-primary/30 bg-primary/5 text-primary": props.toast.type === "info",
      }}
      style={dragStyle()}
      {...handlers}
    >
      <div class="flex min-w-0 items-center gap-3">
        <Show when={props.toast.type === "success"}>
          <CheckCircle2 class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "error"}>
          <AlertCircle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "warning"}>
          <AlertCircle class="h-5 w-5 shrink-0" />
        </Show>
        <Show when={props.toast.type === "info"}>
          <Info class="h-5 w-5 shrink-0" />
        </Show>
        <span class="text-sm font-medium break-words">{props.toast.message}</span>
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <Show when={props.toast.action}>
          {(action) => (
            <button
              type="button"
              data-testid="toast-action"
              onClick={() => {
                removeToast(props.toast.id);
                action().onAction();
              }}
              class="rounded-md px-2 py-1 text-sm font-semibold underline-offset-2 hover:underline hover:bg-black/5 transition-colors"
            >
              {action().label}
            </button>
          )}
        </Show>
        <button
          type="button"
          onClick={() => {
            removeToast(props.toast.id);
          }}
          class="shrink-0 p-1 hover:bg-black/5 rounded-full transition-colors"
        >
          <X class="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function Toaster() {
  return (
    <Portal>
      {/* Anchored to the bottom on every breakpoint. On mobile it spans the
          viewport width minus the horizontal safe-area insets (no x-overflow,
          never under the notch); from `sm` up it shrinks to a fixed width pinned
          to the right edge, matching the original desktop placement.
          Arbitrary-value utilities (not inline styles) so the `sm:` overrides
          actually win. */}
      <div class="fixed z-[300] flex flex-col gap-2 bottom-[calc(1rem+var(--safe-area-inset-bottom))] left-[calc(0.75rem+var(--safe-area-inset-left))] right-[calc(0.75rem+var(--safe-area-inset-right))] sm:left-auto sm:w-full sm:max-w-[400px]">
        <TransitionGroup name="toast">
          <For each={toasts}>{(toast) => <ToastItem toast={toast} />}</For>
        </TransitionGroup>
      </div>
    </Portal>
  );
}
